import { Fragment } from "preact";
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "preact/hooks";

import { Markdown } from "./markdown";
import type { Card, Entry, StreamEvent } from "./types";

const STORAGE_KEY = "vm_agent_session";
/**
 * Capability token for the stored session id.
 *
 * The id alone no longer opens a transcript — the server requires this too, so
 * an id leaking through a log or a shared link discloses nothing on its own.
 */
const TOKEN_KEY = "vm_agent_token";
/**
 * Whether the panel was open when the visitor left the page.
 *
 * Without this, following a link to the practice's memberships or FAQ page drops
 * them back on a closed launcher. The transcript survived, but it looks like it
 * didn't — which loses exactly the thing that separates us from the incumbent.
 */
const OPEN_KEY = "vm_agent_open";

const SUGGESTIONS = [
  "I want to become a patient",
  "How much do the programs cost?",
  "I've been exhausted for months — can you help?",
  "What happens at the first consultation?",
];

const GREETING =
  "Hello — I can answer questions about the practice, walk you through the health " +
  "assessment right here, and get you booked with Dr. Major. What brings you in?";

/**
 * What the closed launcher says.
 *
 * "Become a patient" asked for a decision before the visitor had any reason to
 * make one — it is the last step of the funnel used as the label on the first.
 * Someone still working out whether this practice is for them reads that and
 * does not click, and most visitors are exactly that person.
 *
 * Asking a question costs nothing, so that is the invitation. Booking is still
 * where the conversation goes; it just is not the price of starting one.
 *
 * Override per-site with `data-label` on the script tag.
 */
const DEFAULT_LAUNCHER_LABEL = "Ask a question";

interface Props {
  apiBase: string;
  launcherLabel?: string;
}

let seq = 0;
const nextId = () => `e${++seq}`;

export function App({ apiBase, launcherLabel = DEFAULT_LAUNCHER_LABEL }: Props) {
  const [open, setOpen] = useState(() => {
    try {
      return localStorage.getItem(OPEN_KEY) === "1";
    } catch {
      return false;
    }
  });
  const [entries, setEntries] = useState<Entry[]>([]);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [streamingId, setStreamingId] = useState<string | null>(null);
  const [activity, setActivity] = useState<string | null>(null);
  const [progress, setProgress] = useState<{ percent: number; step: string } | null>(null);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const tokenRef = useRef<string | null>(null);
  const [pinned, setPinned] = useState(true);

  const logRef = useRef<HTMLDivElement>(null);
  const composerRef = useRef<HTMLTextAreaElement>(null);
  const abortRef = useRef<AbortController | null>(null);

  // Rehydrate: this is what keeps the conversation alive across page navigation.
  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    const storedToken = localStorage.getItem(TOKEN_KEY);
    if (!stored || !storedToken) return;
    setSessionId(stored);
    tokenRef.current = storedToken;

    fetch(`${apiBase}/api/session?sessionId=${encodeURIComponent(stored)}`, {
      headers: { "x-vm-session-token": storedToken },
    })
      .then((r) => r.json())
      .then((data) => {
        if (!data?.session) {
          localStorage.removeItem(STORAGE_KEY);
          localStorage.removeItem(TOKEN_KEY);
          tokenRef.current = null;
          setSessionId(null);
          return;
        }
        setEntries(
          data.session.messages.map((m: { role: string; content: string }) => ({
            id: nextId(),
            role: m.role === "user" ? "user" : "bot",
            text: m.content,
          })),
        );
        if (data.session.assessmentPercent > 0) {
          setProgress({ percent: data.session.assessmentPercent, step: "Assessment" });
        }
      })
      .catch(() => {
        /* first load, or server down — start fresh */
      });
  }, [apiBase]);

  /**
   * Follow the stream, but only while the reader is already at the bottom.
   * Yanking someone back down mid-sentence because a token arrived is the
   * single most irritating thing a streaming chat UI can do.
   */
  useLayoutEffect(() => {
    const el = logRef.current;
    if (el && pinned) el.scrollTop = el.scrollHeight;
  }, [entries, activity, open, pinned]);

  const onLogScroll = useCallback(() => {
    const el = logRef.current;
    if (!el) return;
    setPinned(el.scrollHeight - el.scrollTop - el.clientHeight < 48);
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem(OPEN_KEY, open ? "1" : "0");
    } catch {
      /* private mode — the panel just won't reopen after navigation */
    }
  }, [open]);

  // Focus the composer when the panel opens; Escape closes it.
  useEffect(() => {
    if (!open) return;
    composerRef.current?.focus();

    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  // Grow the composer with its content instead of scrolling inside one line.
  useLayoutEffect(() => {
    const el = composerRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, 132)}px`;
  }, [draft, open]);

  const attachCard = useCallback((card: Card) => {
    setEntries((prev) => {
      const next = [...prev];
      for (let i = next.length - 1; i >= 0; i--) {
        if (next[i].role === "bot") {
          next[i] = { ...next[i], card };
          return next;
        }
      }
      return [...next, { id: nextId(), role: "bot", text: "", card }];
    });
  }, []);

  const send = useCallback(
    async (raw: string) => {
      const message = raw.trim();
      if (!message || busy) return;

      setDraft("");
      setBusy(true);
      setActivity(null);
      setPinned(true);

      const botId = nextId();
      setStreamingId(botId);
      setEntries((prev) => [
        ...prev,
        { id: nextId(), role: "user", text: message },
        { id: botId, role: "bot", text: "" },
      ]);

      // Tracked only to clear the "thinking" line the moment real text lands.
      let firstToken = false;

      const controller = new AbortController();
      abortRef.current = controller;

      try {
        const res = await fetch(`${apiBase}/api/chat`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            sessionId,
            sessionToken: tokenRef.current,
            message,
            originUrl: location.href,
          }),
          signal: controller.signal,
        });

        if (!res.ok || !res.body) throw new Error(`Request failed (${res.status})`);

        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buffer = "";

        for (;;) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split("\n\n");
          buffer = lines.pop() ?? "";

          for (const line of lines) {
            if (!line.startsWith("data: ")) continue;
            const payload = line.slice(6);
            if (payload === "[DONE]") continue;

            let event: StreamEvent;
            try {
              event = JSON.parse(payload) as StreamEvent;
            } catch {
              continue;
            }

            switch (event.type) {
              case "session":
                setSessionId(event.sessionId);
                tokenRef.current = event.sessionToken;
                localStorage.setItem(STORAGE_KEY, event.sessionId);
                localStorage.setItem(TOKEN_KEY, event.sessionToken);
                break;

              case "text": {
                if (!firstToken) {
                  firstToken = true;
                  setActivity(null);
                }
                setEntries((prev) =>
                  prev.map((e) => (e.id === botId ? { ...e, text: e.text + event.delta } : e)),
                );
                break;
              }

              case "tool-start":
                setActivity(event.label);
                break;

              case "intake-progress":
                setProgress({ percent: event.percent, step: event.step });
                break;

              case "readiness":
                attachCard({
                  kind: "readiness",
                  score: event.score,
                  band: event.band,
                  drivers: event.drivers,
                  labs: event.labs,
                });
                break;

              case "program":
                attachCard({
                  kind: "program",
                  name: event.name,
                  price: event.price,
                  includes: event.includes,
                });
                break;

              case "slots":
                attachCard({
                  kind: "slots",
                  date: event.date,
                  timezone: event.timezone,
                  times: event.times,
                  typeName: event.typeName,
                });
                break;

              case "booked":
                attachCard({
                  kind: "booked",
                  whenText: event.whenText,
                  typeName: event.typeName,
                  confirmationUrl: event.confirmationUrl,
                  checkoutUrl: event.checkoutUrl,
                  simulated: event.simulated,
                  smsSent: event.smsSent,
                  smsSimulated: event.smsSimulated,
                });
                break;

              case "portal":
                attachCard({
                  kind: "portal",
                  signupUrl: event.signupUrl,
                  hasCode: event.hasCode,
                });
                break;

              case "call-handoff":
                attachCard({ kind: "call", callbackNumber: event.callbackNumber });
                break;

              case "error":
                setEntries((prev) => [
                  ...prev.filter((e) => !(e.id === botId && !e.text && !e.card)),
                  { id: nextId(), role: "error", text: event.message },
                ]);
                break;

              case "done":
                setActivity(null);
                break;
            }
          }
        }
      } catch (err) {
        if ((err as Error).name !== "AbortError") {
          setEntries((prev) => [
            ...prev.filter((e) => !(e.id === botId && !e.text && !e.card)),
            {
              id: nextId(),
              role: "error",
              text: "I couldn't reach the practice's assistant. Please try again in a moment.",
            },
          ]);
        }
      } finally {
        setBusy(false);
        setStreamingId(null);
        setActivity(null);
        abortRef.current = null;
        // Drop the empty bubble if the turn produced nothing at all.
        setEntries((prev) => prev.filter((e) => !(e.id === botId && !e.text && !e.card)));
        composerRef.current?.focus();
      }
    },
    [apiBase, attachCard, busy, sessionId],
  );

  useEffect(() => () => abortRef.current?.abort(), []);

  if (!open) {
    return (
      <div class="root">
        <button
          class="launcher"
          onClick={() => setOpen(true)}
          aria-label="Open the patient assistant"
        >
          <span class="launcher-dot" />
          <ChatIcon />
          {/* Wrapped so the label can be truncated on a narrow screen. A
              practice can set any `data-label`, and an unwrapped text node
              gives the stylesheet nothing to constrain. */}
          <span class="launcher-label">{launcherLabel}</span>
        </button>
      </div>
    );
  }

  const showSuggestions = entries.length === 0 && !busy;

  // Slot cards stay in the transcript once a consultation is booked, and their
  // buttons were live for the rest of the conversation — so tapping a time again
  // sent "Book me for 7:00 AM…" a second time and put a second real appointment
  // on the practitioner's calendar. Once anything is booked, they are inert.
  const bookingDone = entries.some((e) => e.card?.kind === "booked");

  return (
    <div class="root">
      <div class="panel" role="dialog" aria-label="Vitality Medicine RX patient assistant">
        <div class="header">
          <div class="avatar">
            VM
            <span class="status" />
          </div>
          <div class="title">
            <b>Vitality Medicine RX</b>
            <span>Patient intake &amp; booking</span>
          </div>
          <button class="iconbtn" onClick={() => setOpen(false)} aria-label="Close">
            <CloseIcon />
          </button>
        </div>

        <div class="disclaimer">
          Educational information only — not medical advice, diagnosis, or treatment. No
          doctor-patient relationship is created. If this is an emergency, call 911.
        </div>

        {progress && progress.percent > 0 && (
          <div class="progress">
            <div class="progress-head">
              <span>{progress.step}</span>
              <span>{progress.percent}% complete</span>
            </div>
            <div class="progress-track">
              <div class="progress-fill" style={{ width: `${progress.percent}%` }} />
            </div>
          </div>
        )}

        <div class="log-wrap">
          <div class="log" ref={logRef} onScroll={onLogScroll} aria-live="polite">
            {entries.length === 0 && (
              <div class="row bot">
                <div class="bubble-avatar">VM</div>
                <div class="msg bot">
                  <Markdown text={GREETING} />
                </div>
              </div>
            )}

            {entries.map((entry) => (
              <Fragment key={entry.id}>
                {entry.role === "user" && entry.text && (
                  <div class="row user">
                    <div class="msg user">{entry.text}</div>
                  </div>
                )}

                {entry.role === "error" && (
                  <div class="row bot">
                    <div class="bubble-avatar err">!</div>
                    <div class="msg err">{entry.text}</div>
                  </div>
                )}

                {entry.role === "bot" && (entry.text || entry.id === streamingId) && (
                  <div class="row bot">
                    <div class="bubble-avatar">VM</div>
                    {entry.text ? (
                      <div class="msg bot">
                        <Markdown text={entry.text} />
                      </div>
                    ) : (
                      <div class="msg bot typing" aria-label="Assistant is typing">
                        <i /> <i /> <i />
                      </div>
                    )}
                  </div>
                )}

                {entry.card && (
                  <CardView
                    card={entry.card}
                    onPick={(text) => void send(text)}
                    busy={busy}
                    bookingDone={bookingDone}
                  />
                )}
              </Fragment>
            ))}

            {activity && (
              <div class="activity">
                <span class="spinner" />
                {activity}
              </div>
            )}
          </div>

          {!pinned && (
            <button
              class="jump"
              onClick={() => {
                setPinned(true);
                const el = logRef.current;
                if (el) el.scrollTop = el.scrollHeight;
              }}
              aria-label="Jump to the latest message"
            >
              <DownIcon />
            </button>
          )}
        </div>

        {showSuggestions && (
          <div class="chips">
            {SUGGESTIONS.map((s) => (
              <button key={s} class="chip" onClick={() => void send(s)}>
                {s}
              </button>
            ))}
          </div>
        )}

        <div class="composer">
          <div class="field">
            <textarea
              ref={composerRef}
              value={draft}
              placeholder="Ask a question…"
              rows={1}
              onInput={(e) => setDraft((e.target as HTMLTextAreaElement).value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  void send(draft);
                }
              }}
            />
            {busy ? (
              <button
                class="send stop"
                onClick={() => abortRef.current?.abort()}
                aria-label="Stop generating"
              >
                <StopIcon />
              </button>
            ) : (
              <button
                class="send"
                disabled={!draft.trim()}
                onClick={() => void send(draft)}
                aria-label="Send"
              >
                <SendIcon />
              </button>
            )}
          </div>
        </div>

        {/* The attribution is permanent — it is the line the incumbent uses to put
            their name on the practice's site, and the one place ours belongs.
            A time-to-first-token readout used to sit beside it; it was removed
            because a millisecond counter on a patient-facing widget reads as
            engineering talk to the person it is shown to. The number still
            matters, so it is measured in scripts/chat-test.mjs instead. */}
        <div class="footer">
          <a
            class="brand"
            href="https://s10.ai"
            target="_blank"
            rel="noopener noreferrer"
          >
            Powered by <b>s10.ai</b>
          </a>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------- pieces */

/** "07:00" → "7:00 AM" — the scheduler speaks 24-hour, patients don't. */
function to12h(time: string): string {
  const [h, m] = time.split(":").map(Number);
  const suffix = h < 12 ? "AM" : "PM";
  return `${h % 12 === 0 ? 12 : h % 12}:${String(m).padStart(2, "0")} ${suffix}`;
}

interface CardProps {
  card: Card;
  onPick: (text: string) => void;
  busy: boolean;
  /** A consultation is already booked, so stale slot buttons must not re-fire. */
  bookingDone: boolean;
}

function CardView({ card, onPick, busy, bookingDone }: CardProps) {
  if (card.kind === "readiness") {
    return (
      <div class="card">
        <h4>Your health readiness score</h4>
        <div class="score">
          <b>{card.score}</b>
          <span>/ 100</span>
          <span class="band" data-band={card.band}>
            {card.band}
          </span>
        </div>
        <div class="meter">
          <div class="meter-fill" data-band={card.band} style={{ width: `${card.score}%` }} />
        </div>
        {card.drivers.length > 0 && (
          <>
            <p>What's driving it:</p>
            <ul>
              {card.drivers.map((d) => (
                <li key={d}>{d}</li>
              ))}
            </ul>
          </>
        )}
        {card.labs.length > 0 && (
          <>
            <p>Labs the practice would suggest:</p>
            <ul>
              {card.labs.map((l) => (
                <li key={l}>{l}</li>
              ))}
            </ul>
          </>
        )}
      </div>
    );
  }

  if (card.kind === "program") {
    return (
      <div class="card">
        <h4>Suggested program</h4>
        <div class="program-name">{card.name}</div>
        <div class="program-price">{card.price}</div>
        <ul>
          {card.includes.slice(0, 5).map((i) => (
            <li key={i}>{i}</li>
          ))}
        </ul>
      </div>
    );
  }

  // Real openings, tappable. Picking one answers the agent in the visitor's own
  // voice, so the conversation keeps going instead of branching off to a form.
  if (card.kind === "slots") {
    const day = new Date(`${card.date}T00:00:00Z`).toLocaleDateString("en-US", {
      weekday: "long",
      day: "numeric",
      month: "long",
      timeZone: "UTC",
    });
    return (
      <div class="card">
        <h4>{card.typeName}</h4>
        <p>
          {day} — {card.times.length} time{card.times.length === 1 ? "" : "s"} open
        </p>
        <div class="slots">
          {card.times.map((t) => (
            <button
              key={t}
              class="slot"
              disabled={busy || bookingDone}
              onClick={() => onPick(`Book me for ${to12h(t)} on ${day}.`)}
            >
              {to12h(t)}
            </button>
          ))}
        </div>
        <p class="note">
          {bookingDone
            ? "Already booked — ask if you'd like to change it."
            : "Times shown in the practice's timezone."}
        </p>
      </div>
    );
  }

  if (card.kind === "booked") {
    return (
      <div class="card confirmed">
        <h4>{card.simulated ? "Consultation held (demo)" : "Consultation booked"}</h4>
        <div class="program-name">{card.whenText}</div>
        <div class="program-price">{card.typeName}</div>
        {card.checkoutUrl && (
          <a class="cta" href={card.checkoutUrl} target="_blank" rel="noopener noreferrer">
            Complete payment to hold this time
          </a>
        )}
        {!card.checkoutUrl && card.confirmationUrl && (
          <a class="cta" href={card.confirmationUrl} target="_blank" rel="noopener noreferrer">
            View your confirmation
          </a>
        )}
        {card.smsSent && (
          <p class="note">
            {card.smsSimulated
              ? "A confirmation text would be sent to your phone."
              : "We've texted the details to your phone."}
          </p>
        )}
        {card.simulated && (
          <p class="note">Simulated — nothing was written to the practice's calendar.</p>
        )}
      </div>
    );
  }

  // Booking is what creates the account, so this card never claims one exists
  // before a consultation is booked, and never asks for a password.
  // `hasCode` on the wire means "already booked" — see explain_portal_signup.
  if (card.kind === "portal") {
    const booked = card.hasCode;
    return (
      <div class="card">
        <h4>Patient portal</h4>
        <p>
          {booked
            ? "Your login was emailed along with your booking confirmation. You'll set your own password the first time you sign in."
            : "The portal is where your care plan and results live. Booking a consultation creates your login — it's emailed with your confirmation."}
        </p>
        <a class="cta" href={card.signupUrl} target="_blank" rel="noopener noreferrer">
          {booked ? "Go to the patient portal" : "See the patient portal"}
        </a>
      </div>
    );
  }

  return (
    <div class="card">
      <h4>Continue by phone</h4>
      <p>Call the practice and whoever answers already knows what we discussed.</p>
      <a class="cta" href={`tel:${card.callbackNumber.replace(/\D/g, "")}`}>
        Call {card.callbackNumber}
      </a>
    </div>
  );
}

function ChatIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
      <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
      <path d="M18 6 6 18M6 6l12 12" />
    </svg>
  );
}

function SendIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
      <path d="m22 2-7 20-4-9-9-4 20-7z" />
    </svg>
  );
}

function StopIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <rect x="7" y="7" width="10" height="10" rx="2" />
    </svg>
  );
}

function DownIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
      <path d="M12 5v14M19 12l-7 7-7-7" />
    </svg>
  );
}
